/**
 * Whether the stored module has moved on since the editor read it.
 *
 * @module components/studio/module-stored-copy
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { ModuleWorkspaceRecord, ProgrammePlatformClient } from "@/platform";
import { useRealtime } from "@/platform/realtime-context";

export function useStoredCopy(
  client: ProgrammePlatformClient,
  programmeId: string,
  moduleId: string,
  changeCaseId: string | undefined,
  record: ModuleWorkspaceRecord | null,
): { behindStoredCopy: boolean; noteAdopted: () => void } {
  // The hash of the stored copy when it was last re-read, or null when what is
  // on screen is known to be current.
  const [storedHash, setStoredHash] = useState<string | null>(null);
  const adoptions = useRef(0);

  // What is on screen is now what is stored, so any earlier answer about the
  // stored copy describes a version nobody is looking at any more.
  const noteAdopted = useCallback(() => {
    setStoredHash(null);
    adoptions.current += 1;
  }, []);

  // The shell above holds the connection and publishes which module this tab
  // has open; this editor only reads the room.
  const presence = useRealtime();

  // Somebody else saved this module since it was read. Not a conflict on its
  // own: a save composes per property, so it only becomes one where the same
  // property moved on both sides. The room follows the live programme, so its
  // hashes say nothing about a change case's working copy.
  const announced = changeCaseId
    ? undefined
    : presence.savedElsewhere.find((entry) => entry.moduleId === moduleId);
  const knownStoredHash = announced?.moduleContentHash ?? storedHash;
  const behindStoredCopy = Boolean(
    record && knownStoredHash !== null && knownStoredHash !== record.moduleContentHash,
  );

  // Once the copy on screen carries the announced hash the announcement has
  // done its job, or the room's list would only ever grow.
  const { acknowledgeSaved } = presence;
  useEffect(() => {
    if (announced && record?.moduleContentHash === announced.moduleContentHash) {
      acknowledgeSaved(moduleId);
    }
  }, [acknowledgeSaved, announced, moduleId, record?.moduleContentHash]);

  useEffect(() => {
    let active = true;
    // Coming back to the tab is a reason to doubt the copy on screen that no
    // announcement covers: the channel may have been down when the save was
    // made. A record adopted either side of this answer outdates it.
    const check = async (): Promise<void> => {
      const adopted = adoptions.current;
      try {
        const fresh = await client.getModuleWorkspace(programmeId, moduleId, changeCaseId);
        if (active && adopted === adoptions.current) {
          setStoredHash(fresh.moduleContentHash);
        }
      } catch {
        // Leave the last answer standing rather than claiming freshness.
      }
    };
    window.addEventListener("focus", check);
    return () => {
      active = false;
      window.removeEventListener("focus", check);
    };
  }, [changeCaseId, client, moduleId, programmeId]);

  return { behindStoredCopy, noteAdopted };
}
